import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getBlogPostsByCategory, getRecentPosts, getAllCategories } from '../firebase/models/blogService';
import BlogSidebar from '../components/blog/BlogSidebar';

const BlogContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const PageHeader = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: var(--spacing-md);
  color: var(--accent-blue);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-blue);
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-lg);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const BlogImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center;
`;

const BlogContent = styled.div`
  padding: var(--spacing-lg);
`;

const BlogTitle = styled.h2`
  margin-top: 0;
  margin-bottom: var(--spacing-md);
  font-size: 1.5rem;
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: var(--accent-blue);
    }
  }
`;

const BlogMeta = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  color: #666;
  font-size: 0.9rem;
  margin-bottom: var(--spacing-md);
`;

const BlogExcerpt = styled.p`
  margin-bottom: var(--spacing-md);
  color: #444;
`;

const ReadMoreLink = styled(Link)`
  display: inline-block;
  color: var(--accent-blue);
  font-weight: 500;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-xl) 0;
  
  p {
    margin-bottom: var(--spacing-lg);
    color: #666;
  }
`;

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const truncate = (text, length = 150) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const BlogCategory = () => {
  const { categoryId } = useParams();
  
  const [posts, setPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch categories first to get the current category
        const categoriesData = await getAllCategories();
        setCategories(categoriesData);
        
        // Find the current category
        const category = categoriesData.find(cat => 
          cat.slug === categoryId || cat.id === categoryId
        );
        
        if (!category) {
          setError('Category not found');
          setLoading(false);
          return;
        }
        
        setCurrentCategory(category);
        
        // Fetch posts for this category
        const postsData = await getBlogPostsByCategory(category.id);
        setPosts(postsData.filter(post => post.published));
        
        // Fetch recent posts for sidebar
        const recentPostsData = await getRecentPosts(5);
        setRecentPosts(recentPostsData.filter(post => post.published));
        
        setError(null);
      } catch (err) {
        console.error('Error fetching category data:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [categoryId]);
  
  return (
    <BlogContainer>
      <Container>
        <MainContent>
          <PageHeader>
            <BackLink to="/blog">← Back to Blog</BackLink>
            <PageTitle>
              Category: <span>{currentCategory ? currentCategory.name : 'Loading...'}</span>
            </PageTitle>
          </PageHeader>
          
          {loading ? (
            <p>Loading posts...</p>
          ) : error ? (
            <p>{error}</p>
          ) : posts.length === 0 ? (
            <EmptyState>
              <p>No posts found in this category.</p>
              <BackLink to="/blog">Back to All Posts</BackLink>
            </EmptyState>
          ) : (
            <BlogGrid>
              {posts.map(post => (
                <BlogCard key={post.id}>
                  <BlogImage image={post.featuredImage} />
                  <BlogContent>
                    <BlogTitle>
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </BlogTitle>
                    <BlogMeta>
                      <span>{formatDate(post.createdAt)}</span>
                    </BlogMeta>
                    <BlogExcerpt>{truncate(post.excerpt || post.content)}</BlogExcerpt>
                    <ReadMoreLink to={`/blog/${post.slug}`}>Read More</ReadMoreLink>
                  </BlogContent>
                </BlogCard>
              ))}
            </BlogGrid>
          )}
        </MainContent>
        
        <BlogSidebar 
          recentPosts={recentPosts} 
          categories={categories}
        />
      </Container>
    </BlogContainer>
  );
};

export default BlogCategory; 